/**
 * Button — the single button used everywhere, so variant/size/loading look
 * identical across pages instead of each page hand-rolling its own.
 *
 *   variant  primary | secondary | ghost | danger | success
 *   size     sm | md | lg
 *   loading  swaps the leading icon for a spinner and disables interaction —
 *            the label stays, so the button keeps its width and doesn't jump.
 *
 * Icons are Font Awesome class names (e.g. "fa-plus"), matching the rest of
 * the app. `iconRight` moves it to the trailing edge.
 */
import styles from "./Button.module.scss";
import { Spinner } from "../Spinner";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "success";
type Size = "sm" | "md" | "lg";

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Font Awesome name, e.g. "fa-plus". */
  icon?: string | undefined;
  iconRight?: boolean;
  /** Stretch to the container width. */
  block?: boolean;
}

const SPINNER_SIZE: Record<Size, number> = { sm: 13, md: 15, lg: 17 };

export const Button: React.FC<Props> = ({
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  iconRight = false,
  block = false,
  disabled,
  className,
  children,
  ...rest
}) => {
  const cls = [
    styles.btn,
    styles[variant],
    styles[size],
    block ? styles.block : "",
    className ?? "",
  ].filter(Boolean).join(" ");

  const glyph = loading ? (
    <Spinner size={SPINNER_SIZE[size]} />
  ) : icon ? (
    <i className={`fas ${icon}`} aria-hidden="true" />
  ) : null;

  return (
    <button className={cls} disabled={disabled || loading} aria-busy={loading} {...rest}>
      {!iconRight && glyph}
      {children != null && <span className={styles.label}>{children}</span>}
      {iconRight && glyph}
    </button>
  );
};
